import type { TFunction } from "i18next";
import { ApiError } from "@/lib/api-client";

import type { MarketplaceOrderStatus } from "../types/marketplace.types";

export const NO_APPROVED_RECIPIENT_CODE = "NO_APPROVED_RECIPIENT";
export const NO_TARGET_RATIOS_CODE = "NO_TARGET_RATIOS";

const CREATE_ORDER_UNAVAILABLE_STATUSES = new Set<MarketplaceOrderStatus>([
  "PENDING_SEND",
  "SENDING",
  "FAILED",
  "AWAITING_VENDOR",
  "SENT",
  "NOT_REQUIRED",
]);

/** 503 create-order body may include the persisted row so the dialog can retry. */
export function readCreateOrderUnavailable(error: unknown): {
  orderId: string;
  status: MarketplaceOrderStatus;
} | null {
  if (!(error instanceof ApiError) || !error.orderId) return null;
  const raw = error.orderStatus;
  const status =
    raw && CREATE_ORDER_UNAVAILABLE_STATUSES.has(raw as MarketplaceOrderStatus)
      ? (raw as MarketplaceOrderStatus)
      : "PENDING_SEND";
  return { orderId: error.orderId, status };
}

export function isRecoverableOrderCreateError(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    (error.code === "TIMEOUT" ||
      error.code === "NETWORK_ERROR" ||
      error.code === "SERVICE_UNAVAILABLE" ||
      error.status === 503)
  );
}

export function createOrderErrorMessage(error: unknown, t: TFunction): string {
  if (error instanceof ApiError && error.code === NO_APPROVED_RECIPIENT_CODE) {
    return t(
      "opportunities.scripts.sendDialog.noApprovedEmail",
      "Production partner has no approved contact email — contact support",
    );
  }
  if (error instanceof ApiError && error.code === NO_TARGET_RATIOS_CODE) {
    return t(
      "marketplace.orders.sendToPartner.noTargetRatios",
      "Pick at least one target ratio before sending this order to another partner.",
    );
  }
  return t("common.error.generic", "Something went wrong. Please try again.");
}

export function createOrderTimeoutErrorMessage(t: TFunction): string {
  return t(
    "opportunities.scripts.sendDialog.confirmTimeout",
    "We couldn't confirm the send within 5 minutes. Check Marketplace orders or try again.",
  );
}

